import React, { useEffect, useRef, useState, useCallback } from 'react';
import './VideoPlayer.css';

export default function YouTubePlayer({ videoId, initialState, roomId, emit, on }) {
  const playerRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const [syncStatus, setSyncStatus] = useState('');
  const [isPlaying, setIsPlaying] = useState(initialState?.playing || false);
  const [currentTime, setCurrentTime] = useState(initialState?.currentTime || 0);
  const [duration, setDuration] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  
  const suppressRemoteRef = useRef(false);
  const lastSeekEmitRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  const lastServerTimeRef = useRef(initialState?.currentTime || 0);
  const lastServerTimeSetAtRef = useRef(Date.now());
  const isServerPlayingRef = useRef(initialState?.playing || false);
  const lastCorrectionTimeRef = useRef(0);
  const lastPlayPauseEmitRef = useRef(0);
  const playPauseDebounceRef = useRef(null);
  // Кто последний инициировал действие
  const lastActionByRef = useRef(null);
  // Последнее video_state от сервера, пришедшее до инициализации плеера
  const pendingStateRef = useRef(null);

  const showSync = useCallback((msg) => {
    setSyncStatus(msg);
    setTimeout(() => setSyncStatus(''), 1500);
  }, []);

  // Загрузка YouTube API
  useEffect(() => {
    // Уничтожаем старый инстанс перед созданием нового
    if (playerInstanceRef.current) {
      try { playerInstanceRef.current.destroy(); } catch (_) {}
      playerInstanceRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    const tryInit = () => {
      if (window.YT && window.YT.Player) {
        setTimeout(() => initPlayer(), 0);
        return;
      }

      if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        window.onYouTubeIframeAPIReady = () => setTimeout(() => initPlayer(), 0);
        return;
      }

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      window.onYouTubeIframeAPIReady = () => setTimeout(() => initPlayer(), 0);
      document.head.appendChild(tag);
    };

    tryInit();

    return () => {
      if (playPauseDebounceRef.current) clearTimeout(playPauseDebounceRef.current);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (playerInstanceRef.current) {
        try { playerInstanceRef.current.destroy(); } catch (_) {}
        playerInstanceRef.current = null;
      }
    };
  }, [videoId]);

  const initPlayer = () => {
    console.log('[YT] initPlayer called, playerRef:', playerRef.current, 'existing:', playerInstanceRef.current);

    if (!playerRef.current || playerInstanceRef.current) return;
    console.log('[YT] creating YT.Player for videoId:', videoId);
    playerInstanceRef.current = new window.YT.Player(playerRef.current, {
      videoId: videoId,
      playerVars: {
        autoplay: initialState?.playing ? 1 : 0,
        controls: 1,
        rel: 0,
        modestbranding: 1,
        enablejsapi: 1,
        origin: window.location.origin,
        start: Math.floor(initialState?.currentTime || 0)
      },
      
      events: {
        onReady: (event) => {
          console.log('[YT] onReady fired');
          console.log('[YT] onReady, pending:', pendingStateRef.current);
          console.log('[YT] initialState:', initialState);
          setPlayerReady(true);
          const player = event.target;
          setDuration(player.getDuration());

          // Применяем video_state, пришедший до инициализации плеера (race condition fix)
          const pending = pendingStateRef.current;
          if (pending && (pending.currentTime > 0 || pending.isPlaying)) {
            setTimeout(() => {
              if (!playerInstanceRef.current?.seekTo) return;
              const { currentTime: t = 0, isPlaying: playing = false } = pending;
              suppressRemoteRef.current = true;
              lastServerTimeRef.current = t;
              player.seekTo(t, true);
              if (playing) {
                player.playVideo();
                setIsPlaying(true);
              } else {
                player.pauseVideo();
                setIsPlaying(false);
              }
              showSync('Синхронизировано');
              setTimeout(() => { suppressRemoteRef.current = false; }, 1500);
            }, 1000); // ждём секунду пока буфер загрузится
          }

          startPolling(player);
        },
        onStateChange: (event) => {
          const player = event.target;
          const state = event.data;
          const time = player.getCurrentTime();
          
          // Игнорируем если это удаленная команда
          if (suppressRemoteRef.current) return;

          const now = Date.now();
          
          // Защита от слишком частых отправок (не чаще раза в секунду)
          if (state === 1 || state === 2) {
            const isPlay = state === 1;
            if (playPauseDebounceRef.current) clearTimeout(playPauseDebounceRef.current);
            playPauseDebounceRef.current = setTimeout(() => {
              const currentTime = playerInstanceRef.current?.getCurrentTime?.() ?? time;
              lastPlayPauseEmitRef.current = Date.now();
              lastActionByRef.current = 'me';
              setIsPlaying(isPlay);
              if (isPlay) {
                emit('video_play', { roomId, currentTime, by: 'me' });
                showSync('Вы запустили видео');
              } else {
                emit('video_pause', { roomId, currentTime, by: 'me' });
                showSync('⏸ Вы поставили паузу');
              }
            }, 300);
          }
        },
        onError: (event) => {
          console.error('YouTube error:', event.data);
          console.error('[YT] error:', event.data);
        }
      }
    });
    console.log('[YT] player instance created:', playerInstanceRef.current);
    console.log('[YT] playerRef after init:', playerRef.current);
  };

  const startPolling = (player) => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    
    pollingIntervalRef.current = setInterval(() => {
      if (!player || !player.getCurrentTime) return;
      
      try {
        const time = player.getCurrentTime();
        setCurrentTime(time);
        
        // Плавная синхронизация с сервером (только если не подавлено)
        if (!suppressRemoteRef.current && playerReady) {
          // Вычисляем ожидаемую позицию сервера с учётом прошедшего времени:
          // lastServerTimeRef — снимок на момент последнего video_state,
          // поэтому прибавляем прошедшее с тех пор время (если видео играет).
          const elapsed = isServerPlayingRef.current
            ? (Date.now() - lastServerTimeSetAtRef.current) / 1000
            : 0;
          const expectedServerTime = lastServerTimeRef.current + elapsed;
          const diff = Math.abs(time - expectedServerTime);

          // Если разница слишком большая (> 5 сек) и прошло больше 5 секунд после последней коррекции
          if (diff > 5 && Date.now() - lastCorrectionTimeRef.current > 5000) {
            player.seekTo(expectedServerTime, true);
            lastCorrectionTimeRef.current = Date.now();
            showSync('Синхронизация');
          }
        }
      } catch (e) {}
    }, 2000); // Проверяем реже - раз в 2 секунды
  };

  // Слушаем события от других пользователей
  useEffect(() => {
    const offPlay = on('video_play', ({ currentTime: t, by }) => {

      if (!playerInstanceRef.current || by === 'me') return;

      suppressRemoteRef.current = true;
      lastActionByRef.current = by;
      lastServerTimeRef.current = t;
      lastServerTimeSetAtRef.current = Date.now();
      isServerPlayingRef.current = true;

      playerInstanceRef.current.seekTo(t, true);
      playerInstanceRef.current.playVideo();
      setIsPlaying(true);
      showSync(`${by || 'Участник'} запустил видео`);

      setTimeout(() => { suppressRemoteRef.current = false; }, 1000);
    });

    const offPause = on('video_pause', ({ currentTime: t, by }) => {

      if (!playerInstanceRef.current || by === 'me') return;

      suppressRemoteRef.current = true;
      lastActionByRef.current = by;
      lastServerTimeRef.current = t;
      lastServerTimeSetAtRef.current = Date.now();
      isServerPlayingRef.current = false;

      playerInstanceRef.current.seekTo(t, true);
      playerInstanceRef.current.pauseVideo();
      setIsPlaying(false);
      showSync(`⏸ ${by || 'Участник'} поставил паузу`);

      setTimeout(() => { suppressRemoteRef.current = false; }, 1000);
    });

    const offSeek = on('video_seek', ({ currentTime: t, by }) => {

      if (!playerInstanceRef.current || by === 'me') return;

      suppressRemoteRef.current = true;
      lastActionByRef.current = by;
      lastServerTimeRef.current = t;
      lastServerTimeSetAtRef.current = Date.now();

      playerInstanceRef.current.seekTo(t, true);
      setCurrentTime(t);
      showSync(`⏩ ${by || 'Участник'} перемотал`);

      setTimeout(() => { suppressRemoteRef.current = false; }, 1000);
    });

    const offVideoState = on('video_state', (state) => {
      console.log('[YT] video_state received, playerReady:', playerInstanceRef.current?.seekTo ? 'yes' : 'no', state);
      if (!state) return;
      console.log('[YT] video_state received, playerReady:', playerInstanceRef.current?.seekTo ? 'yes' : 'no', state);
      const { currentTime: serverTime = 0, isPlaying: serverIsPlaying = false } = state;

      // Всегда сохраняем — если плеер ещё не готов, применим в onReady
      pendingStateRef.current = state;

      // Обновляем снимок и время его получения для корректного drift-correction
      lastServerTimeRef.current = serverTime;
      lastServerTimeSetAtRef.current = Date.now();
      isServerPlayingRef.current = serverIsPlaying;
      setCurrentTime(serverTime);

      // Плеер ещё не инициализирован — состояние применим в onReady
      if (!playerInstanceRef.current?.seekTo) return;

      // Если дрейф небольшой — не делаем перемотку чтобы не прерывать воспроизведение
      const currentPlayerTime = playerInstanceRef.current.getCurrentTime?.() ?? 0;
      const drift = Math.abs(serverTime - currentPlayerTime);
      const needsSeek = drift > 3;

      suppressRemoteRef.current = true;

      if (!playerInstanceRef.current?.seekTo) {
        suppressRemoteRef.current = false;
        return;
      }
      if (needsSeek) {
        playerInstanceRef.current.seekTo(serverTime, true);
      }

      if (serverIsPlaying) {
        playerInstanceRef.current.playVideo();
        setIsPlaying(true);
      } else {
        playerInstanceRef.current.pauseVideo();
        setIsPlaying(false);
      }

      showSync('Синхронизировано');
      setTimeout(() => { suppressRemoteRef.current = false; }, needsSeek ? 1500 : 300);
    });

    return () => {
      offPlay();
      offPause();
      offSeek();
      offVideoState();
    };
  }, [on, showSync, roomId]);

  const handlePlay = () => {
    if (!playerInstanceRef.current) return;
    playerInstanceRef.current.playVideo();
  };

  const handlePause = () => {
    if (!playerInstanceRef.current) return;
    playerInstanceRef.current.pauseVideo();
  };

  const handleSeek = (e) => {
    if (!duration || !playerInstanceRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const t = ratio * duration;
    
    lastActionByRef.current = 'me';
    playerInstanceRef.current.seekTo(t, true);
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="video-player">
      <div className="video-wrapper">
        <div
          ref={playerRef}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#0f0f0f'
          }}
        />
        {syncStatus && <div className="sync-toast">{syncStatus}</div>}
        {!playerReady && (
          <div className="video-overlay">
            <div className="loading-spinner" />
            <span>Загрузка YouTube плеера...</span>
          </div>
        )}
      </div>

      <div className="video-controls">
        <div className="progress-bar" onClick={handleSeek}>
          <div className="progress-fill" style={{ width: `${progress}%` }} />
          <div className="progress-thumb" style={{ left: `${progress}%` }} />
        </div>
        <div className="controls-row">
          <button className="ctrl-btn" onClick={isPlaying ? handlePause : handlePlay}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          <span className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          <div className="controls-right">
            {playerReady && (
              <span className="player-status ready">● YouTube</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
